import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import '../../assignment.scss'
import { Alert, Button, Flex, Form, Space, Spin } from 'antd'
import FinetunePatternSelect from './FinetunePatternSelect'
import TextArea from 'antd/es/input/TextArea'
import { useReviewSubmissionMutation } from '../../../../store/apis/submission.api'
import { useParams } from 'react-router'
import { useFetchSubmissionCriteriaDetailsQuery } from '../../../../store/apis/semantic-language.api'
import { fetchSubmissionCriteriaDetailsSelectors } from '../../../../store/apis/utils/selectors/semantic-language.api.selectors'
import { isNumber } from 'lodash'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import { Perm, State } from '../../../../constants/assignment-permissions'
import Access from '../access/Access'
import { getColorByIndex } from '../../../../utils/colors.helper'
import Text from 'antd/es/typography/Text'

const useBindReasoningToPattern = (form, patternId, currentPatternId, currentReasoning) => {
    useEffect(() => {
        if (currentPatternId === patternId) form.setFieldValue('reasoning', currentReasoning)
    }, [patternId, currentReasoning, currentPatternId])
}

const ReviewSubmissionForm = ({ onSuccess, onCancel }) => {
    const id = useSelector(submissionSelectors.openedSubmissionId)
    const patterns = useSelector(patternSelectors.all)
    const drawer = useSelector(state => state.assignment.drawer)

    const { token, layerId } = useParams()
    const [form] = Form.useForm()
    const patternId = Form.useWatch('patternId', form)
    const reasoning = Form.useWatch('reasoning', form)
    const currentPatternId = drawer?.file?.motifList?.[0]?.motif_id
    const pattern = patterns.find(p => p.motif_id === (isNumber(patternId) ? patternId : currentPatternId))

    const [reviewSubmission, reviewSubmissionResult] = useReviewSubmissionMutation({
        fixedCacheKey: `reviewSubmission-${id}`,
    })
    const { data: currentReasoning, isFetching: isFetchingCurrentReasoning } = useFetchSubmissionCriteriaDetailsQuery(
        { token, layerId, submissionId: drawer.file?.id },
        {
            skip: !drawer.file,
            selectFromResult: result =>
                fetchSubmissionCriteriaDetailsSelectors.selectReasoningByPatternId(
                    { patternId: currentPatternId },
                    result,
                ),
        },
    )

    useBindReasoningToPattern(form, patternId, currentPatternId, currentReasoning)

    const correctedByUser = currentPatternId !== patternId || currentReasoning !== reasoning

    useEffect(() => {
        handleRevertCorrections()
    }, [id])
    const handleSubmit = async fields => {
        const response = await reviewSubmission({
            token,
            layerId,
            submissionId: id,
            newPatternId: fields.patternId,
            newReasoning: [fields.reasoning],
            removePattern: false,
            isPatternCorrected: currentPatternId !== fields.patternId,
            isReasoningCorrected: currentReasoning !== fields.reasoning,
        })
        if ('data' in response) onSuccess()
    }

    const handleRemovePattern = async () => {
        const response = await reviewSubmission({
            token,
            layerId,
            submissionId: id,
            newPatternId: null,
            newReasoning: null,
            removePattern: true,
        })
        if ('data' in response) onSuccess()
    }

    const handleRevertCorrections = () => {
        form.setFieldsValue({ patternId: currentPatternId, reasoning: currentReasoning })
    }

    return (
        <Form
            name='review-submission-form'
            autoComplete='off'
            form={form}
            onFinish={handleSubmit}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                name='patternId'
                label='Pattern'
                rules={[{ required: true, message: 'Please, select a pattern' }]}
                initialValue={currentPatternId}
            >
                <Access
                    rules={[Perm.CAN_EDIT]}
                    fallback={
                        <Space size={8}>
                            <div
                                style={{
                                    backgroundColor: getColorByIndex(pattern?.color_id).value,
                                    borderRadius: 4,
                                    minWidth: 22,
                                    minHeight: 22,
                                }}
                            />
                            {pattern?.motif_caption}
                        </Space>
                    }
                >
                    <FinetunePatternSelect />
                </Access>
            </Form.Item>
            {isNumber(patternId) && (
                <Spin spinning={isFetchingCurrentReasoning}>
                    <Form.Item
                        name='reasoning'
                        label='Personalized Feedback'
                        rules={[{ required: true, message: 'Please, enter the personalized feedback' }]}
                    >
                        <Access
                            rules={[Perm.CAN_EDIT]}
                            fallback={<Text>{reasoning || 'This submission has no personalized feedback'}</Text>}
                        >
                            <TextArea rows={4} />
                        </Access>
                    </Form.Item>
                </Spin>
            )}
            {reviewSubmissionResult.isError && (
                <Alert message={reviewSubmissionResult?.error} type='error' showIcon closable />
            )}
            <Access rules={[Perm.CAN_EDIT]}>
                <Flex gap={8} justify='space-between'>
                    <Flex gap={8}>
                        <Button type='primary' htmlType='submit' loading={reviewSubmissionResult?.isLoading} data-test="approve-submission-button">
                            {correctedByUser ? 'Approve with corrections' : 'Approve'}
                        </Button>
                        <Button onClick={handleRemovePattern} danger loading={reviewSubmissionResult?.isLoading}>
                            Remove pattern
                        </Button>
                    </Flex>
                    <Flex gap={8}>
                        {correctedByUser && <Button onClick={handleRevertCorrections}>Revert Corrections</Button>}
                        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
                    </Flex>
                </Flex>
            </Access>
        </Form>
    )
}

export default ReviewSubmissionForm
